import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const RealTimeContext = createContext();

export const useRealTime = () => {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error('useRealTime must be used within RealTimeProvider');
  }
  return context;
};

const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes in milliseconds

export const RealTimeProvider = ({ children }) => {
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(REFRESH_INTERVAL_MS);
  const [subscribers, setSubscribers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [nextRefreshIn, setNextRefreshIn] = useState(REFRESH_INTERVAL_MS);

  // Subscribe to refresh events
  const subscribe = useCallback((callback) => {
    setSubscribers(prev => [...prev, callback]);
    return () => {
      setSubscribers(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  // Add alert
  const addAlert = useCallback((alert) => {
    const newAlert = {
      id: Date.now(),
      timestamp: new Date(),
      read: false,
      ...alert
    };
    setAlerts(prev => [newAlert, ...prev].slice(0, 50)); // Keep last 50 alerts
  }, []);

  // Mark alert as read
  const markAlertRead = useCallback((alertId) => {
    setAlerts(prev => prev.map(a => 
      a.id === alertId ? { ...a, read: true } : a
    ));
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Manual refresh trigger
  const triggerRefresh = useCallback(() => {
    setLastRefresh(new Date());
    subscribers.forEach(callback => {
      try {
        callback();
      } catch (err) {
        console.error('Refresh subscriber error:', err);
      }
    });
    setNextRefreshIn(refreshInterval);
  }, [subscribers, refreshInterval]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshEnabled(prev => !prev);
  }, []);

  // Update refresh interval (in minutes)
  const updateRefreshInterval = useCallback((minutes) => {
    const ms = minutes * 60 * 1000;
    setRefreshInterval(ms);
    setNextRefreshIn(ms);
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (!isAutoRefreshEnabled) return;

    const interval = setInterval(() => {
      triggerRefresh();
      
      // Simulate checking for new alerts (in production, this would be from backend)
      const randomAlertChance = Math.random();
      if (randomAlertChance < 0.1) { // 10% chance of new alert on each refresh
        const alertTypes = [
          { type: 'warning', title: 'NRW Spike Detected', message: 'NRW increased by 2% in Margao area' },
          { type: 'info', title: 'Flow Rate Normal', message: 'All WTPs operating within normal range' },
          { type: 'error', title: 'Quality Alert', message: 'pH level deviation detected at Verna OHT' },
        ];
        const randomAlert = alertTypes[Math.floor(Math.random() * alertTypes.length)];
        addAlert(randomAlert);
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [isAutoRefreshEnabled, refreshInterval, triggerRefresh, addAlert]);

  // Countdown timer effect
  useEffect(() => {
    if (!isAutoRefreshEnabled) return;

    const countdown = setInterval(() => {
      setNextRefreshIn(prev => {
        if (prev <= 1000) return refreshInterval;
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [isAutoRefreshEnabled, refreshInterval]);

  const value = {
    lastRefresh,
    isAutoRefreshEnabled,
    refreshInterval,
    nextRefreshIn,
    alerts,
    unreadAlertCount: alerts.filter(a => !a.read).length,
    subscribe,
    triggerRefresh,
    toggleAutoRefresh,
    updateRefreshInterval,
    addAlert,
    markAlertRead,
    clearAlerts,
  };

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  );
};
